import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const Navbar = () => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const role = localStorage.getItem("role");

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (confirmLogout) {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      window.location.reload();
      navigate("/login");
    }
  };

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const closeDropdown = () => setDropdownOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-white shadow-md px-4 py-3 md:px-8 flex justify-between items-center relative z-50">
      {/* Logo */}
      <Link to="/" className="text-xl font-extrabold text-orange-500 tracking-wide">
        ABCD
      </Link>

      {/* Mobile toggle */}
      <div className="md:hidden">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-gray-700 focus:outline-none"
        >
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Desktop Menu */}
      <div className="hidden md:flex items-center gap-4">
        {role === "admin" && (
          <div className="flex items-center gap-4" ref={dropdownRef}>
            <button
              onClick={() => navigate("/list")}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-all duration-200"
            >
              List
            </button>

            <button
              onClick={() => navigate("/reports")}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition-all duration-200"
            >
              Reports
            </button>

            <div className="relative">
              <button
                onClick={toggleDropdown}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-1"
              >
                Users <ChevronDown size={16} />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl z-10 overflow-hidden">
                  <button
                    onClick={() => {
                      navigate("/all-users");
                      closeDropdown();
                    }}
                    className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm font-medium"
                  >
                    View all clients
                  </button>
                  <button
                    onClick={() => {
                      navigate("/all-brokers");
                      closeDropdown();
                    }}
                    className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm font-medium"
                  >
                    View all brokers
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-medium transition-all duration-200"
        >
          Logout
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="absolute top-full left-0 w-full bg-white shadow-xl mt-3 rounded-lg md:hidden px-6 py-4 space-y-3 z-40"
        >
          {role === "admin" && (
            <>
              <div>
                <p className="text-gray-500 font-semibold text-sm mb-1">Admin</p>
                <button
                  onClick={() => {
                    navigate("/list");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-800 font-medium"
                >
                  📄 List
                </button>
                <button
                  onClick={() => {
                    navigate("/reports");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mt-2 text-left px-4 py-3 rounded-md bg-green-50 hover:bg-green-100 text-green-800 font-medium"
                >
                  📊 Reports
                </button>
              </div>

              <div className="pt-4 border-t">
                <p className="text-gray-500 font-semibold text-sm mb-1">Users</p>
                <button
                  onClick={() => {
                    navigate("/all-users");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-md hover:bg-gray-100 text-gray-700 font-medium"
                >
                  👥 View all clients
                </button>
                <button
                  onClick={() => {
                    navigate("/all-brokers");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-md hover:bg-gray-100 text-gray-700 font-medium"
                >
                  💼 View all brokers
                </button>
              </div>
            </>
          )}

          <div className="pt-4 border-t">
            <button
              onClick={handleLogout}
              className="w-full text-left bg-red-100 hover:bg-red-200 text-red-800 font-medium px-4 py-3 rounded-md"
            >
              🚪 Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
